<script setup>
import {ref, computed, onMounted, watch} from 'vue';
import { useFetch } from "@/plugins/fetch";
import { useMainStore } from "@/store/MainStore";
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import {useRoute} from "vue-router";

dayjs.extend(relativeTime);

const fetch = useFetch();
const store = useMainStore();

const showMenu = computed(() => store.topnavOpenMenu === 'notifications');
const notifications = ref([]);
const darkMode = computed(() => store.darkMode);
const unreadCount = computed(() => notifications.value.length);

const getBackgroundClass = (severity) => {
  const map = {
    success: 'bg-success-soft',
    info: 'bg-info-soft',
    warning: 'bg-warning-soft',
    danger: 'bg-danger-soft',
  };
  return map[severity] || 'bg-secondary-soft';
};

const toggleMenu = () => store.setTopnavOpenMenu('notifications');
const closeMenu = () => store.closeTopnavMenu();

const fetchNotifications = async () => {
  // Don't fetch on auth pages or when not logged in
  if (route.meta.layout === 'auth' || store.layout === 'auth') {
    return;
  }
  try {
    const response = await fetch('user/notifications/');
    if (Array.isArray(response)) {
      notifications.value = response
          .filter(notification => !notification.is_read)
          .map(notification => ({
        ...notification,
        timeAgo: dayjs(notification.created_at.date).fromNow()
      }));
    }
  } catch (error) {
    // Silently fail - user may not be authenticated
  }
};

const clearNotification = async (id) => {
  try {
    await fetch(`user/notifications/${id}/clear/`, { method: 'POST' });
    notifications.value = notifications.value.filter(n => n.id !== id);
  } catch (error) {
    console.error('Error clearing notification:', error);
  }
};

const route = useRoute();

onMounted(() => {
  if (route.meta.layout !== 'auth' && store.layout !== 'auth') {
    fetchNotifications();
  }
});

watch(
    () => route.meta.layout,
    (newLayout) => {
      if (newLayout !== 'auth') {
        fetchNotifications();
      }
    }
)
</script>

<template>
  <li class="nav-item dropdown d-flex align-items-center pe-2">
    <a
        href="#"
        class="p-0 nav-link notification-trigger"
        :class="[showMenu ? 'show is-open' : '', darkMode ? 'text-white' : 'text-dark']"
        id="dropdownMenuButton"
        data-bs-toggle="dropdown"
        data-bs-auto-close="outside"
        aria-expanded="false"
        @click.stop="toggleMenu"
        @blur="closeMenu"
    >
      <i class="cursor-pointer fa fa-bell" :class="darkMode ? 'text-white' : 'text-dark'"></i>
      <span class="badge badge-sm badge-circle badge-floating badge-primary border-white">{{ unreadCount }}</span>
    </a>
    <ul
        class="px-2 py-3 dropdown-menu dropdown-menu-end me-sm-n4 notification-menu"
        :class="showMenu ? 'show' : ''"
        aria-labelledby="dropdownMenuButton"
    >
      <li class="menu-header d-flex align-items-center justify-content-between px-2 mb-2">
        <span>Notifications</span>
        <span class="count-pill">{{ unreadCount }} new</span>
      </li>
      <li v-if="notifications.length === 0" class="text-center text-muted py-3">
        <i class="fa fa-bell-slash me-2"></i> Nothing to see here
      </li>
      <li v-for="notification in notifications" :key="notification.id" class="mb-2">
        <div class="dropdown-item border-radius-md notification-item">
          <div class="py-1 d-flex align-items-center">
            <div class="my-auto avatar avatar-sm me-3 icon-pill" :class="getBackgroundClass(notification.severity)">
              <i :class="notification.icon || 'fa fa-bell'"></i>
            </div>
            <div class="d-flex flex-column justify-content-center me-3">
              <h6 class="mb-1 text-sm font-weight-normal">
                {{ notification.message }}
              </h6>
              <p class="mb-0 text-xs text-secondary">
                <i class="fa fa-clock me-1"></i>
                {{ notification.timeAgo }}
              </p>
            </div>
            <button @click.stop="clearNotification(notification.id)" v-if="!notification.is_global" class="btn btn-link text-danger p-0 ms-auto">
               <i class="fa fa-times"></i>
            </button>
          </div>
        </div>
      </li>
    </ul>
  </li>
</template>

<style scoped>
.dropdown-item {
  display: flex;
  justify-content: space-between;
  border: 1px solid var(--calid-border, #e5e7eb);
  box-shadow: 0 2px 6px rgba(0,0,0,0.04);
}

.dropdown-item .avatar {
  display: flex;
  align-items: center;
  justify-content: center;
}

.btn-link {
  margin-left: auto;
}

.py-1.d-flex {
  display: flex;
  align-items: center;
}

.notification-menu {
  min-width: 320px;
  border-radius: 14px;
  border: 1px solid var(--calid-border, rgba(255,255,255,0.08));
  background: var(--calid-card-bg, rgba(17,24,39,0.96));
  box-shadow: 0 22px 50px rgba(0,0,0,0.35);
  backdrop-filter: blur(10px);
}

.notification-trigger.is-open {
  background: rgba(59, 130, 246, 0.08);
  border-radius: 10px;
  box-shadow: 0 6px 16px rgba(59, 130, 246, 0.25);
}

.notification-item {
  background: var(--calid-bg-tertiary, rgba(255,255,255,0.04));
  transition: transform 0.15s ease, box-shadow 0.15s ease, border-color 0.15s ease;
  border: 1px solid transparent;
}

.notification-item:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 12px rgba(0,0,0,0.12);
  //border-color: rgba(0, 212, 255, 0.3);
}

.icon-pill {
  border-radius: 12px;
  background: #e5e7eb;
}

.bg-success-soft { background: #ecfdf3; color: #15803d; }
.bg-info-soft { background: #e0f2fe; color: #0369a1; }
.bg-warning-soft { background: #fff7ed; color: #c2410c; }
.bg-danger-soft { background: #fef2f2; color: #b91c1c; }
.bg-secondary-soft { background: #eef2ff; color: #4338ca; }

.menu-header {
  font-weight: 600;
  color: var(--calid-text-primary, #f0f0f5);
}

.count-pill {
  padding: 0.2rem 0.6rem;
  border-radius: 999px;
  background: rgba(0,212,255,0.16);
  color: #00d4ff;
  font-size: 0.8rem;
  font-weight: 700;
}
</style>
